import React, { useState } from "react"
import styled from "styled-components"
import { graphql } from "gatsby"
import axios from "axios"
import { format } from "date-fns"
import { useIntl } from "gatsby-plugin-react-intl"

import { Formik } from "formik"
import * as Yup from "yup"

import Layout from "../../layouts/Layout"
import { Container, Row, Col } from "../../components/Grid"
import PageHeader from "../../components/PageHeader"
import {
  Form,
  Input,
  Select,
  DatePicker,
  SubmitOverlay,
  RequiredNotice,
} from "../../components/FormFields"
import Button from "../../components/Button"
import Spacer from "../../components/Spacer"
import Snippet from "../../components/Snippet"
import Seo from "../../components/Seo"

import { media, mixins } from "../../styles"
import { toTitleCase } from "../../helpers"
import mastercardCurrencies from "../../data/mastercardCurrencies.json"

const Wrapper = styled.div`
  background: ${({ theme }) => theme.greyLight};
  text-align: center;
  padding: 45px 10px 65px;
  position: relative;

  @media ${media.md} {
    margin: 0 0 20px;
    padding: 45px 0 65px;
    text-align: left;
  }

  @media ${media.lg} {
    padding: 60px 0 65px;
  }

  @media ${media.xl} {
    padding: 90px 0 65px;
  }
`

const Disclaimer = styled.div`
  opacity: 0.5;

  p {
    ${mixins.copy5}
  }
`

const Results = styled.div`
  background: ${({ theme }) => theme.white};
  padding: 30px;

  h3 {
    ${mixins.title5}
  }

  p {
    ${mixins.copy4}
  }
`

export const pageQuery = graphql`
  query MastercardCalculatorPageQuery(
    $language: String
    $originalPath: String
  ) {
    contentfulPageMetadata(
      pagePath: { eq: $originalPath }
      node_locale: { eq: $language }
    ) {
      ...PageMetadata
    }
  }
`

const Calculator = () => {
  const intl = useIntl()
  const currencies = mastercardCurrencies.data.currencies
  const [result, setResult] = useState()

  const getCurrencyName = (key) => {
    return toTitleCase(currencies.find((item) => item.alphaCd === key).currName)
  }

  const submitForm = async (values) => {
    setResult(null)
    const request = {
      ...values,
      rateDate: format(
        // parse(values.rateDate, "dd.MM.yyyy", new Date()),
        values.rateDate,
        "yyyy-MM-dd"
      ),
      crdhldBillCurr: "EUR",
    }

    try {
      const response = await axios.get(
        `https://h4t3mir9ek.execute-api.eu-central-1.amazonaws.com/default/?rate_date=${request.rateDate}&trans_curr=${request.transCurr}&trans_amt=${request.transAmt}&crdhld_bill_curr=${request.crdhldBillCurr}&bank_fee_pct=${request.bankFeePct}&bank_fee_fixed=${request.bankFeeFixed}`
      )
      setResult(response.data.summary.data)
    } catch (error) {
      console.log(error)
    }
  }

  return (
    <Formik
      initialValues={{
        transAmt: "",
        transCurr: "",
        rateDate: new Date(),
        bankFeePct: "",
        bankFeeFixed: "",
        // crdhldBillCurr: "EUR",
      }}
      validationSchema={Yup.object().shape({
        transAmt: Yup.number()
          .transform((o, v) => parseFloat(v.replace(/,/g, ".")))
          .typeError(
            intl.formatMessage({
              id: "mastercardCalculator.errorNumber",
            })
          )
          .positive(
            intl.formatMessage({
              id: "mastercardCalculator.errorNumberPositive",
            })
          )
          .required(
            intl.formatMessage({
              id: "mastercardCalculator.errorTransAmtMissing",
            })
          ),
        transCurr: Yup.string().required(
          intl.formatMessage({
            id: "mastercardCalculator.errorTransCurrMissing",
          })
        ),
        rateDate: Yup.mixed()
          .nullable()
          .required(
            intl.formatMessage({
              id: "mastercardCalculator.errorRateDateMissing",
            })
          ),
        bankFeePct: Yup.number()
          .transform((o, v) => parseFloat(v.replace(/,/g, ".")))
          .typeError(
            intl.formatMessage({
              id: "mastercardCalculator.errorNumber",
            })
          )
          .min(
            0,
            intl.formatMessage({
              id: "mastercardCalculator.errorNumberPositive",
            })
          ),
        bankFeeFixed: Yup.number()
          .transform((o, v) => parseFloat(v.replace(/,/g, ".")))
          .typeError(
            intl.formatMessage({
              id: "mastercardCalculator.errorNumber",
            })
          )
          .min(
            0,
            intl.formatMessage({
              id: "mastercardCalculator.errorNumberPositive",
            })
          ),
      })}
      onSubmit={async (values, { setSubmitting }) => {
        await submitForm(values)
        setSubmitting(false)
      }}
    >
      {({ handleSubmit, isSubmitting, setFieldValue }) => (
        <>
          <RequiredNotice>
            {intl.formatMessage({
              id: "mastercardCalculator.requiredFields",
            })}
          </RequiredNotice>
          <Form onSubmit={handleSubmit} data-testid="mastercard-calculator">
            <Row>
              <Col md="6">
                <Input
                  name="transAmt"
                  label={intl.formatMessage({
                    id: "mastercardCalculator.transAmt",
                  })}
                  placeholder="10000,00"
                  required
                />
              </Col>
              <Col md="6">
                <Select
                  name="transCurr"
                  label={intl.formatMessage({
                    id: "mastercardCalculator.transCurr",
                  })}
                  required
                >
                  <option
                    value=""
                    aria-label={intl.formatMessage({
                      id: "mastercardCalculator.pleaseSelect",
                    })}
                  >
                    &nbsp;
                  </option>
                  {currencies.map((currency) => (
                    <option value={currency.alphaCd} key={currency.alphaCd}>
                      {`${toTitleCase(currency.currName)} (${
                        currency.alphaCd
                      })`}
                    </option>
                  ))}
                </Select>
              </Col>
            </Row>
            <Spacer height={30} md={50} />
            <Row>
              <Col md="4">
                <DatePicker
                  name="rateDate"
                  label={intl.formatMessage({
                    id: "mastercardCalculator.rateDate",
                  })}
                  maxDate={new Date()}
                  required
                />
              </Col>
              <Col md="4">
                <Input
                  name="bankFeePct"
                  label={intl.formatMessage({
                    id: "mastercardCalculator.bankFeePct",
                  })}
                  placeholder="0,0"
                  extra="%"
                />
              </Col>
              <Col md="4">
                <Input
                  name="bankFeeFixed"
                  label={intl.formatMessage({
                    id: "mastercardCalculator.bankFeeFixed",
                  })}
                  placeholder="0,00"
                  extra="€"
                />
              </Col>
            </Row>
            <Spacer height={30} />
            <p>
              {intl.formatMessage({
                id: "mastercardCalculator.crdhldBillCurr",
              })}
              : EUR
            </p>
            <Spacer height={60} />
            <Button as="button" type="submit" arrow>
              {intl.formatMessage({
                id: "mastercardCalculator.submit",
              })}
            </Button>
            <Spacer height={30} md={60} />
            {result && (
              <>
                <Results data-testid="mastercard-results">
                  <h3>
                    {intl.formatMessage({
                      id: "mastercardCalculator.results",
                    })}
                  </h3>
                  <p>
                    {result.transAmt} {getCurrencyName(result.transCurr)} ={" "}
                    <strong>
                      <span data-testid="final-result">
                        {result.mastercard.crdhldBillAmtInclAllFees}
                      </span>{" "}
                      {getCurrencyName(result.crdhldBillCurr)}
                    </strong>
                  </p>
                  <p>
                    1 {getCurrencyName(result.transCurr)} ={" "}
                    <strong>
                      {result.mastercard.mastercardConvRateExclAllFees}{" "}
                      {getCurrencyName(result.crdhldBillCurr)}
                    </strong>
                    {result.ecb?.ecbReferenceRate && (
                      <>
                        <br />
                        {"% "}
                        {intl.formatMessage({
                          id: "mastercardCalculator.markup",
                        })}
                        {" = "}
                        <strong>
                          {result.pctDifferenceMastercardInclAllFeesAndEcb}
                        </strong>
                      </>
                    )}
                  </p>
                </Results>
                <Spacer height={30} md={40} />
              </>
            )}

            {isSubmitting && <SubmitOverlay />}
          </Form>
          <Disclaimer>
            <p>
              {intl.formatMessage({
                id: "mastercardCalculator.disclaimer",
              })}
            </p>
          </Disclaimer>
        </>
      )}
    </Formik>
  )
}

const MastercardCalculator = (props) => {
  const pageMetadata = props.data.contentfulPageMetadata

  return (
    <Layout pageId="mastercard-calculator">
      <Seo {...pageMetadata} />
      <Container>
        <Wrapper>
          <PageHeader
            header={Snippet({
              id: "mastercardCalculatorHeader",
              field: "header",
            })}
            text={Snippet({
              id: "mastercardCalculatorHeader",
              field: "text",
              md: true,
            })}
          />
          <Spacer height={30} md={60} />
          <Row>
            <Col md="10" mdOffset="1" lg="8" lgOffset="2">
              <Calculator />
            </Col>
          </Row>
        </Wrapper>
      </Container>
    </Layout>
  )
}

export default MastercardCalculator
